import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, CheckCircle, AlertCircle, Download, ChevronDown, ChevronUp, Pen } from 'lucide-react';
import { Button } from '../components/ui/button';
import SignaturePad from '../components/SignaturePad';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const PartnerAgreementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerId = searchParams.get('partner_id');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [partner, setPartner] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  
  const [expandedSections, setExpandedSections] = useState({});
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const agreementRef = useRef(null);
  const signatureRef = useRef(null);

  useEffect(() => {
    if (!partnerId) {
      toast.error('Partner-ID fehlt');
      navigate('/portal/login');
      return;
    }
    
    checkAgreementStatus();
    fetchAgreement();
  }, [partnerId]);

  const checkAgreementStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/status/${partnerId}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.has_signed) {
          setAlreadySigned(true);
          setSignedAt(data.signed_at);
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const fetchAgreement = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/text/${partnerId}`
      );
      if (response.ok) {
        const data = await response.json();
        setAgreement(data.agreement);
        setPartner(data.partner);
      } else {
        toast.error('Vereinbarung konnte nicht geladen werden');
      }
    } catch (error) {
      console.error('Error fetching agreement:', error);
      toast.error('Fehler beim Laden der Vereinbarung');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setHasScrolledToEnd(true);
    }
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const expandAllSections = () => {
    const expanded = {};
    agreement?.sections?.forEach((_, idx) => {
      expanded[idx] = true;
    });
    setExpandedSections(expanded);
    setHasScrolledToEnd(true);
  };

  const generatePDF = async () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Helper function to add text with word wrap
    const addText = (text, fontSize, isBold = false, maxWidth = contentWidth) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      const lines = pdf.splitTextToSize(text.trim(), maxWidth);
      
      lines.forEach(line => {
        if (yPosition > pageHeight - margin - 10) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.text(line, margin, yPosition);
        yPosition += fontSize * 0.4;
      });
      yPosition += 2;
    };

    // Title
    pdf.setFillColor(46, 125, 50);
    pdf.rect(0, 0, pageWidth, 35, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Partnervereinbarung', pageWidth / 2, 20, { align: 'center' });
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Provisionsvereinbarung für Vermittlung von Unfallgutachten', pageWidth / 2, 28, { align: 'center' });
    
    pdf.setTextColor(0, 0, 0);
    yPosition = 45;

    // Preamble
    if (agreement?.preamble) {
      addText(agreement.preamble, 10);
      yPosition += 5;
    }

    // Sections
    agreement?.sections?.forEach((section) => {
      yPosition += 3;
      addText(`${section.number} ${section.title}`, 12, true);
      addText(section.content, 10);
    });

    // Signature section
    yPosition += 10;
    if (yPosition > pageHeight - 80) {
      pdf.addPage();
      yPosition = margin;
    }

    pdf.setDrawColor(46, 125, 50);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    addText('Unterschrift des Partners:', 11, true);
    yPosition += 5;

    // Add signature image
    if (signatureData) {
      try {
        pdf.addImage(signatureData, 'PNG', margin, yPosition, 60, 25);
        yPosition += 30;
      } catch (e) {
        console.error('Error adding signature:', e);
      }
    }

    // Partner info
    addText(`${partner?.company_name || ''}`, 10, true);
    addText(`${partner?.contact_person || ''}`, 10);
    addText(`Datum: ${new Date().toLocaleDateString('de-DE')}`, 10);

    // Footer
    yPosition = pageHeight - 15;
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Stand: ${agreement?.footer || 'März 2026'} | Version ${agreement?.version || '1.0'}`, pageWidth / 2, yPosition, { align: 'center' });
    pdf.text('Dein Gutachten Direkt GmbH | www.dein-gutachten-direkt.de', pageWidth / 2, yPosition + 4, { align: 'center' });

    return pdf.output('datauristring');
  };

  const handleSubmit = async () => {
    if (!signatureData) {
      toast.error('Bitte unterschreiben Sie die Vereinbarung');
      signatureRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (!acceptedTerms) {
      toast.error('Bitte bestätigen Sie die Bedingungen');
      return;
    }

    setSubmitting(true);

    try {
      // Generate PDF
      const pdfDataUri = await generatePDF();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/sign`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            partner_id: partnerId,
            signature_data: pdfDataUri,
            signed_at: new Date().toISOString(),
            ip_address: null,
            user_agent: navigator.userAgent
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('Vereinbarung erfolgreich unterschrieben!');
        setAlreadySigned(true);
        setSignedAt(data.signed_at);
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/portal/login');
        }, 2000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Speichern der Vereinbarung');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/download/${partnerId}`,
      '_blank'
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mx-auto mb-4"></div>
          <p className="text-gray-600">Vereinbarung wird geladen...</p>
        </div>
      </div>
    );
  }

  if (alreadySigned) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>Partnervereinbarung | Dein Gutachten Direkt</title>
        </Helmet>
        
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <div className="w-20 h-20 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-brand-green" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Vereinbarung bereits unterschrieben
            </h1>
            <p className="text-gray-600 mb-2">
              Sie haben die Partnervereinbarung bereits am{' '}
              <strong>{signedAt ? new Date(signedAt).toLocaleDateString('de-DE') : 'früheren Zeitpunkt'}</strong>{' '}
              unterschrieben.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              Eine Kopie wurde an Ihre E-Mail-Adresse gesendet.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleDownload}
                variant="outline"
                className="border-brand-green text-brand-green hover:bg-brand-green/5"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF herunterladen
              </Button>
              <Button
                onClick={() => navigate('/portal/login')}
                className="bg-brand-green hover:bg-brand-green-dark text-white"
              >
                Zum Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Partnervereinbarung unterschreiben | Dein Gutachten Direkt</title>
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-green/10 rounded-xl">
              <FileText className="w-6 h-6 text-brand-green" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Partnervereinbarung</h1>
              <p className="text-sm text-gray-500">Bitte lesen und unterschreiben</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Wichtiger Hinweis</p>
              <p>Bitte lesen Sie die Vereinbarung vollständig durch, bevor Sie unterschreiben. Mit Ihrer digitalen Unterschrift akzeptieren Sie alle Bedingungen dieser Vereinbarung.</p>
            </div>
          </div>
        </div>

        {/* Partner Info */}
        {partner && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <p className="text-sm text-gray-500 mb-1">Vereinbarung für:</p>
            <p className="font-semibold text-gray-900">{partner.company_name || partner.email}</p>
            {partner.contact_person && (
              <p className="text-gray-600">{partner.contact_person}</p>
            )}
          </div>
        )}

        {/* Agreement Content */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
          {/* Title */}
          <div className="bg-gradient-to-r from-brand-green to-brand-green-dark p-6 text-white">
            <h2 className="text-2xl font-bold">{agreement?.title}</h2>
            <p className="opacity-90">{agreement?.subtitle}</p>
          </div>

          {/* Scrollable Content */}
          <div 
            ref={agreementRef}
            onScroll={handleScroll}
            className="max-h-[500px] overflow-y-auto p-6"
          >
            {/* Preamble */}
            <div className="mb-6 whitespace-pre-line text-gray-700 text-sm leading-relaxed">
              {agreement?.preamble}
            </div>

            {/* Expand All Button */}
            <div className="flex justify-end mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={expandAllSections}
                className="text-brand-green hover:text-brand-green-dark"
              >
                Alle Abschnitte öffnen
              </Button>
            </div>

            {/* Sections */}
            {agreement?.sections?.map((section, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl mb-3 overflow-hidden">
                <button
                  onClick={() => toggleSection(idx)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <span className="font-semibold text-gray-900">
                    {section.number} {section.title}
                  </span>
                  {expandedSections[idx] ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {expandedSections[idx] && (
                  <div className="p-4 text-sm text-gray-700 whitespace-pre-line leading-relaxed border-t border-gray-200">
                    {section.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Signature Section */}
        <div 
          ref={signatureRef}
          className="bg-white rounded-2xl border border-gray-200 p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-brand-green/10 rounded-xl">
              <Pen className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Digitale Unterschrift</h3>
              <p className="text-sm text-gray-500">Unterschreiben Sie hier mit Maus oder Finger</p>
            </div>
          </div>

          <SignaturePad
            onSignatureChange={setSignatureData}
            width={600}
            height={200}
          />

          {/* Terms Checkbox */}
          <label className="flex items-start gap-3 mt-6 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-green focus:ring-brand-green"
              data-testid="accept-terms-checkbox"
            />
            <span className="text-sm text-gray-700">
              Ich habe die Partnervereinbarung vollständig gelesen und verstanden. 
              Mit meiner digitalen Unterschrift akzeptiere ich alle Bedingungen 
              und bestätige, dass ich berechtigt bin, für{' '}
              <strong>{partner?.company_name || 'mein Unternehmen'}</strong>{' '}
              rechtsverbindlich zu handeln.
            </span>
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={handleSubmit}
            disabled={submitting || !signatureData || !acceptedTerms}
            className="flex-1 bg-brand-green hover:bg-brand-green-dark text-white py-6 text-lg"
            data-testid="submit-agreement-btn"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Wird gespeichert...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Vereinbarung unterschreiben
              </>
            )}
          </Button>
        </div>

        {/* Footer Note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          {agreement?.footer}
        </p>
      </main>
    </div>
  );
};

export default PartnerAgreementPage;
